import React, { createContext, useReducer } from "react";
import { parseModalKey } from "./libraries/nodejs-manager/src/crud-frontend";
import { get } from "./components/api";

const initialState =
  typeof window === "undefined" ? {} : window.__PRELOADED_STATE__;

export const StateContext = createContext({
  state: initialState,
  dispatch: () => {},
  dispatchSidebar: () => {}
});

const doToggleModalOnly = (state, action) => {
  // eslint-disable-next-line no-unused-vars
  const [_a, _b, _c, data] = parseModalKey({
    [action["data"]["id"]]: true
  });
  if (data) {
    const [first, second] = action["data"]["id"].split("__");
    return {
      ...state,
      modalKey: { [`${first}__${second}`]: action["data"]["value"], data }
    };
  }
  return {
    ...state,
    modalKey: { [action["data"]["id"]]: action["data"]["value"] }
  };
};

const doSortRows = (state, action) => {
  if (!action.data.collection) return state;

  const sortedRows = action.data.sortedRows;
  let [first, second, third] = action.data.collection.split(".");
  if (second && second[0] === "[")
    second = parseInt(second.replace("[", "").replace("]", ""), 10);

  if (third)
    return {
      ...state,
      [first]: {
        ...state[first],
        [second]: { ...state[first][second], [third]: sortedRows }
      }
    };

  if (second)
    return {
      ...state,
      [first]: {
        ...state[first],
        [second]: sortedRows
      }
    };

  return {
    ...state,
    [first]: sortedRows
  };
};

const doToggleModal = (state, action) => {
  const containsModalType =
    action["data"]["id"].indexOf(":") > -1 ? true : false;
  if (containsModalType && state["modalKey"]) {
    // eslint-disable-next-line no-unused-vars
    const [check, _] = action["data"]["id"].split(":");

    state = {
      ...state,
      modalKey: Object.keys(state["modalKey"]).reduce((acc, key) => {
        if (key.indexOf(check) > -1) {
          return acc;
        }
        return { ...acc, [key]: state["modalKey"][key] };
      }, {})
    };
  }
  return {
    ...state,
    modalKey: {
      ...("modalKey" in state ? state["modalKey"] : {}),
      [action["data"]["id"]]: action["data"]["value"]
    }
  };
};

export const StateProvider = ({ children, preloadedState }) => {
  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case "REPLACE_SESSION":
        return { ...state, session: action["data"] };

      case "CHANGE_PAGE":
        return { ...action["data"] };

      case "CHANGE_CONTENT":
        return {
          ...action["data"],
          location: state["location"],
          modalKey: state["modalKey"]
        };

      case "TOGGLE_MODAL_ONLY":
        return doToggleModalOnly(state, action);

      case "TOGGLE_SIDEBAR":
        return {
          ...state,
          ...(action?.extraData || {}),
          sidebar: action["data"]
        };

      case "TOGGLE_MODAL":
        return doToggleModal(state, action);

      case "SORT_ROWS":
        return doSortRows(state, action);

      case "SEARCH_ROWS":
        return {
          ...state,
          ["search_rows"]: action.data.rows,
          ["search_visible"]: action.data.visible,
          ["search_interview_rounds"]: action.data.interviewRounds,
          ["search_flags"]: action.data.flags,
          ["search_requirements"]: action.data.requirements || {},
          ["search_disciplines"]: action.data.disciplines || {},
          ["search_interviewers_by_id"]: action.data.interviewersById || {}
        };

      case "TOGGLE_SORT_ROWS":
        return {
          ...state,
          sortState: {
            ...(state.sortState ? state.sortState : {}),
            [action.data]: !(state.sortState ? state.sortState : {})[
              action.data
            ]
          }
        };

      default:
        throw new Error();
    }
  }, preloadedState || initialState);

  const dispatchSidebar = ({
    identifier,
    openState = true,
    data = {},
    widthClass = "max-w-3xl",
    extraData = {}
  }) => {
    dispatch({
      type: "TOGGLE_SIDEBAR",
      data: {
        identifier,
        openState,
        data,
        widthClass
      },
      extraData
    });
  };

  const pageRefresher = async (
    additionalPageData = {},
    hashTag,
    refetch = true
  ) => {
    const { data } = refetch
      ? await get(window.location.href, {})
      : { data: {} };
    dispatch({
      type: "CHANGE_CONTENT",
      data: {
        ...data,
        ...additionalPageData
      }
    });
    if (hashTag) window.location.hash = hashTag;
  };

  return (
    <StateContext.Provider
      value={{ state, dispatch, dispatchSidebar, pageRefresher }}
    >
      {children}
    </StateContext.Provider>
  );
};
